import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import InvoiceApi, {
	getFutureAndGeneratedInvoicesQueryKey,
	getUseInvoiceQueryKey,
} from '../../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../../api/PolicyholderApi';
import { AuthData } from '../../context/auth';
import { ConfigData } from '../../context/config';
import { PolicyData } from '../../context/policy';
import {
	PremiumReportData,
	withPremiumReportContext,
} from '../../context/premiumReport';
import {
	PAY_INVOICE_FAILURE,
	PAY_INVOICE_SUCCESS,
	WRITE_OFF_INVOICE_FAILURE,
	WRITE_OFF_INVOICE_SUCCESS,
} from '../../data/labelsAndMessages';
import PaymentsList from '../PaymentsList';
import { errorToast, successToast } from '../Toast';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	premiumReportData: PremiumReportData;
	policyData: PolicyData;
}

const InvoiceView = (props: Props) => {
	const params = useParams();
	const { invoiceId } = params;
	const { authData, premiumReportData, policyData } = props;
	const { policy } = policyData;
	const authToken = authData?.auth?.authorizationToken;
	const { premiumReport, fetchPremiumReport, setLoading } = premiumReportData;
	const queryClient = useQueryClient();
	const [invoice, setInvoice] = useState(null);

	useEffect(() => {
		const fetchInvoice = async () => {
			setLoading(true);
			setInvoice(premiumReport.invoice);
			setLoading(false);
		};
		fetchInvoice();
	}, [invoiceId]);

	const refetchData = () => {
		// invalidate policy
		queryClient.invalidateQueries(
			getUsePolicyQueryKey(policy.locator, authToken),
		);

		// invalidate invoices
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(policy.locator, authToken),
		);
		queryClient.invalidateQueries(
			getInvoicesForPolicyholderQueryKey(authToken, policy.policyholderLocator),
		);

		// invalidate invoice
		queryClient.invalidateQueries(
			getUseInvoiceQueryKey(authToken, invoice.locator),
		);
	};

	const refetchReport = () => {
		fetchPremiumReport(premiumReport.locator);
	};

	const payInvoice = async (
		_policyLocator: string,
		locator: string,
		req: any,
	) => {
		setLoading(true);
		try {
			await InvoiceApi.payInvoice(
				authData.auth.authorizationToken,
				locator,
				req,
			);
			refetchData();
			refetchReport();
			successToast(PAY_INVOICE_SUCCESS());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(PAY_INVOICE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	const reversePayment = async (paymentLocator: string) => {
		setLoading(true);
		try {
			await InvoiceApi.reversePayment(
				authData.auth.authorizationToken,
				paymentLocator,
			);
			refetchData();
			refetchReport();
			successToast('Successfully reversed payment');
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to pay invoice');
		} finally {
			setLoading(false);
		}
	};

	const writeOffInvoice = async (locator: string) => {
		setLoading(true);
		try {
			await InvoiceApi.writeOffInvoice(
				authData.auth.authorizationToken,
				locator,
			);
			refetchData();
			refetchReport();
			successToast(WRITE_OFF_INVOICE_SUCCESS());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(WRITE_OFF_INVOICE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	return (
		<PaymentsList
			invoice={invoice}
			onPayment={payInvoice}
			onReverse={reversePayment}
			onWriteOff={writeOffInvoice}
			callback={refetchReport}
			currency={
				policyData && policyData.policy ? policyData.policy.currency : null
			}
		/>
	);
};

export default withPremiumReportContext<Props>(InvoiceView);
